export type ActivityIconKey = 'image' | 'words' | 'listen' | 'speak' | 'check';
export type ActivityStatus = 'ready' | 'review' | 'draft';

export interface TeacherPrompt {
    id: number;
    title: string;
    detail: string;
}

export interface TeacherActivity {
    id: number;
    title: string;
    type: string;
    duration: number;
    purpose: string;
    icon: ActivityIconKey;
    status: ActivityStatus;
    teacherInstructions: string;
    studentInstructions: string;
    targetLanguage: string[];
    prompts: TeacherPrompt[];
}

export type TargetSource = 'required' | 'suggested' | 'added';

export interface VocabularyTarget {
    hanzi: string;
    pinyin: string;
    meaning: string;
    source: TargetSource;
    isReview?: boolean;
}

export interface GrammarTarget {
    pattern: string;
    meaning: string;
    example: string;
    source: TargetSource;
}

export const defaultBrief = 'Create a 50-minute intermediate Mandarin lesson about ordering food in a restaurant. Students know basic food vocabulary but need practice using measure words and making polite requests.';

export const vocabularyTargets: VocabularyTarget[] = [
    { hanzi: '菜单', pinyin: 'càidān', meaning: 'menu', source: 'required', isReview: true },
    { hanzi: '服务员', pinyin: 'fúwùyuán', meaning: 'server', source: 'required', isReview: true },
    { hanzi: '饺子', pinyin: 'jiǎozi', meaning: 'dumplings', source: 'suggested' },
    { hanzi: '米饭', pinyin: 'mǐfàn', meaning: 'cooked rice', source: 'suggested' },
    { hanzi: '一盘', pinyin: 'yì pán', meaning: 'one plate', source: 'suggested' },
    { hanzi: '一碗', pinyin: 'yì wǎn', meaning: 'one bowl', source: 'suggested' },
    { hanzi: '一杯', pinyin: 'yì bēi', meaning: 'one cup', source: 'suggested' },
    { hanzi: '点菜', pinyin: 'diǎn cài', meaning: 'to order food', source: 'added' },
];

export const grammarTargets: GrammarTarget[] = [
    { pattern: '请给我 + noun', meaning: 'Please give me…', example: '请给我一杯茶。', source: 'required' },
    { pattern: 'number + measure word + noun', meaning: 'Expressing a quantity', example: '一盘饺子 / 一碗米饭', source: 'suggested' },
    { pattern: '您想 + verb + 什么？', meaning: 'What would you like to…?', example: '您想吃什么？', source: 'suggested' },
];

export const lessonActivities: TeacherActivity[] = [
    {
        id: 1,
        title: 'Picture warm-up',
        type: 'Visual discussion',
        duration: 5,
        purpose: 'Activate prior knowledge and introduce the restaurant setting.',
        icon: 'image',
        status: 'ready',
        teacherInstructions: 'Show a restaurant scene. Invite students to name foods, people, and objects they recognize before introducing new language.',
        studentInstructions: 'Look at the restaurant scene. Say three things you notice in Chinese.',
        targetLanguage: ['饭馆', '菜单', '服务员'],
        prompts: [
            { id: 11, title: 'Restaurant scene', detail: 'Image observation' },
            { id: 12, title: 'What do you notice?', detail: 'Open speaking' },
        ],
    },
    {
        id: 2,
        title: 'Vocabulary in context',
        type: 'Vocabulary introduction',
        duration: 8,
        purpose: 'Introduce useful dishes, measure words, and polite request language.',
        icon: 'words',
        status: 'ready',
        teacherInstructions: 'Model each word in a short restaurant phrase. Ask students to repeat the complete phrase rather than the isolated word.',
        studentInstructions: 'Listen and repeat each phrase. Pay attention to the measure word.',
        targetLanguage: ['一碗米饭', '一盘饺子', '一杯茶', '请给我……'],
        prompts: [
            { id: 21, title: 'Introduce key phrases', detail: 'Listen and repeat' },
            { id: 22, title: 'Choose the correct dish', detail: 'Image selection' },
        ],
    },
    {
        id: 3,
        title: 'Listen and respond',
        type: 'Listening comprehension',
        duration: 8,
        purpose: 'Help students recognize polite requests and measure words in speech.',
        icon: 'listen',
        status: 'review',
        teacherInstructions: 'Play each request twice. Ask students to identify both the dish and its measure word.',
        studentInstructions: 'Listen to each order and choose the matching item.',
        targetLanguage: ['我要……', '请来一……', '还要什么？'],
        prompts: [
            { id: 31, title: 'Listen to an order', detail: 'Audio comprehension' },
            { id: 32, title: 'Select the matching meal', detail: 'Comprehension check' },
        ],
    },
    {
        id: 4,
        title: 'Guided restaurant dialogue',
        type: 'Guided speaking',
        duration: 10,
        purpose: 'Rehearse a complete exchange with sentence starters and support.',
        icon: 'speak',
        status: 'ready',
        teacherInstructions: 'Model the exchange once, then let students complete it with a dish and drink of their choice.',
        studentInstructions: 'Complete the restaurant conversation. Use the response starters if you need help.',
        targetLanguage: ['您想吃什么？', '请给我……', '我还要……'],
        prompts: [
            { id: 41, title: 'Teacher models the exchange', detail: 'Conversation listening' },
            { id: 42, title: 'Student completes an order', detail: 'Guided response' },
            { id: 43, title: 'Add a drink', detail: 'Follow-up response' },
        ],
    },
    {
        id: 5,
        title: 'Restaurant role-play',
        type: 'AI conversation',
        duration: 14,
        purpose: 'Apply the target language in a flexible, realistic interaction.',
        icon: 'speak',
        status: 'draft',
        teacherInstructions: 'Students order a meal from the AI waiter. Encourage complete sentences and at least one follow-up question.',
        studentInstructions: 'Order a meal and a drink. Ask one question about the menu before you finish.',
        targetLanguage: ['有什么……？', '请给我……', '一共多少钱？'],
        prompts: [
            { id: 51, title: 'Set the restaurant scene', detail: 'Scenario introduction' },
            { id: 52, title: 'AI waiter greeting', detail: 'Conversation prompt' },
            { id: 53, title: 'Student places an order', detail: 'Open speaking' },
            { id: 54, title: 'Follow-up question', detail: 'AI conversation' },
            { id: 55, title: 'Conversation feedback', detail: 'Summary and feedback' },
        ],
    },
    {
        id: 6,
        title: 'Exit ticket',
        type: 'Quick assessment',
        duration: 5,
        purpose: 'Check whether students can independently make a polite request.',
        icon: 'check',
        status: 'ready',
        teacherInstructions: 'Ask every student to record one complete restaurant request using an appropriate measure word.',
        studentInstructions: 'Record one polite request for a food or drink you would like.',
        targetLanguage: ['请给我……'],
        prompts: [
            { id: 61, title: 'Final spoken request', detail: 'Speaking assessment' },
        ],
    },
];

// Companion practice is intentionally separate from the 50-minute classroom
// procedure above. These are the runnable activities a teacher assigns before
// or after class, not a one-to-one rendering of every classroom stage.
export const exerciseActivities: TeacherActivity[] = [
    {
        id: 101,
        title: 'Vocabulary review',
        type: 'Interactive vocabulary',
        duration: 5,
        purpose: 'Recall the key restaurant words introduced during class.',
        icon: 'words',
        status: 'ready',
        teacherInstructions: 'Assign after class for retrieval practice. Students can repeat the activity until all items are correct.',
        studentInstructions: 'Match each restaurant word to its meaning, then say the complete phrase aloud.',
        targetLanguage: ['菜单', '服务员', '饺子', '米饭'],
        prompts: [
            { id: 1011, title: 'Recall key words', detail: 'Vocabulary cards' },
            { id: 1012, title: 'Match word and picture', detail: 'Image matching' },
        ],
    },
    {
        id: 102,
        title: 'Measure-word listening',
        type: 'Listening practice',
        duration: 5,
        purpose: 'Recognize food measure words in natural spoken requests.',
        icon: 'listen',
        status: 'ready',
        teacherInstructions: 'Assign as independent listening practice after students have encountered the measure words in class.',
        studentInstructions: 'Listen to each order and choose the matching quantity and dish.',
        targetLanguage: ['一盘饺子', '一碗米饭', '一杯茶'],
        prompts: [
            { id: 1021, title: 'Listen to an order', detail: 'Audio comprehension' },
            { id: 1022, title: 'Choose the matching item', detail: 'Image selection' },
        ],
    },
    {
        id: 103,
        title: 'Guided ordering practice',
        type: 'Supported speaking',
        duration: 6,
        purpose: 'Build accuracy with polite requests before open conversation.',
        icon: 'speak',
        status: 'review',
        teacherInstructions: 'Use as a bridge between classroom pair work and independent conversation practice.',
        studentInstructions: 'Complete each request using the pictured food and the correct measure word.',
        targetLanguage: ['请给我……', '我要一……'],
        prompts: [
            { id: 1031, title: 'Hear a model request', detail: 'Listen and repeat' },
            { id: 1032, title: 'Complete the request', detail: 'Guided speaking' },
            { id: 1033, title: 'Try without support', detail: 'Independent speaking' },
        ],
    },
    {
        id: 104,
        title: 'Restaurant role-play',
        type: 'AI conversation',
        duration: 7,
        purpose: 'Apply the class language independently in a realistic exchange.',
        icon: 'speak',
        status: 'draft',
        teacherInstructions: 'Assign after the classroom role-play. Review completion and pronunciation results before the next lesson.',
        studentInstructions: 'Order a meal and a drink. Ask one question about the menu before you finish.',
        targetLanguage: ['有什么……？', '请给我……', '一共多少钱？'],
        prompts: [
            { id: 1041, title: 'Set the restaurant scene', detail: 'Scenario introduction' },
            { id: 1042, title: 'AI waiter greeting', detail: 'Conversation prompt' },
            { id: 1043, title: 'Place an order', detail: 'Open speaking' },
            { id: 1044, title: 'Ask a follow-up', detail: 'AI conversation' },
            { id: 1045, title: 'Review feedback', detail: 'Summary and feedback' },
        ],
    },
    {
        id: 105,
        title: 'Independent exit check',
        type: 'Quick assessment',
        duration: 2,
        purpose: 'Confirm independent control of one polite request after practice.',
        icon: 'check',
        status: 'ready',
        teacherInstructions: 'Use the recording as a quick follow-up check before the next class.',
        studentInstructions: 'Record one complete restaurant request without using a response starter.',
        targetLanguage: ['请给我……'],
        prompts: [
            { id: 1051, title: 'Final spoken request', detail: 'Speaking assessment' },
        ],
    },
];
